import { intersection, isString } from 'core_main/globals/helpers';
import dayjs from 'dayjs';
import { getByPath } from 'helpers/get-by-path';
import {
  SessionDetails,
  AssistantRulesContext,
  CustomerDetails,
  AssistantRule,
  AssistantRuleCriteria,
  AssistantRuleAction,
  AssistantRuleOperator,
} from './types';

const DATE_UNITS = {
  s: 'second',
  h: 'hour',
  d: 'day',
  w: 'week',
  m: 'minute',
  M: 'month',
};

// function isDisabledAttribute(obj, metadata) {
//   const { resource, property, type } = obj;
//   if (
//     type !== 'property' ||
//     resource !== metadata.resource ||
//     !property.startsWith('custom.')
//   ) {
//     return false;
//   }

//   const propName = property.split('.')[1];
//   const propertyData = get(metadata, `properties.${propName}`);

//   if (!propertyData) {
//     return true;
//   }

//   return !!get(propertyData, 'deletedAt');
// }
export function resolve(path: string, context: AssistantRulesContext) {
  const dotPath = path.replace(/_/g, '.');
  return getByPath(context, dotPath);
}

export function isSet(val) {
  if (val === null || val === undefined) {
    return false;
  }
  if (typeof val === 'string') {
    return val.trim() !== '';
  }
  if (Array.isArray(val)) {
    return val.length !== 0;
  }

  return true;
}

export function equals(left, right) {
  let rightCast;

  if (Array.isArray(left) && !Array.isArray(right)) {
    return left.includes(right);
  }

  try {
    switch (typeof left) {
      case 'string':
        rightCast = String(right);
        break;
      case 'number':
        rightCast = Number(right);
        break;
      case 'boolean':
        rightCast = typeof right === 'boolean' ? right : right === 'true';
        break;
      default:
        rightCast = right;
    }
  } catch (e) {
    rightCast = right;
  }
  // eslint-disable-next-line eqeqeq
  return left == rightCast;
}

export function evaluateSingle(op, val) {
  switch (op) {
    case 'is_set': {
      return isSet(val);
    }
    case 'not_set': {
      return !isSet(val);
    }
    default: {
      throw new Error(`operation ${op} not supported`);
    }
  }
}

export function contains(left, right) {
  if (isString(left) && isString(right)) {
    const lowerLeft = (left || '').toLocaleLowerCase();
    const lowerRight = (right || '').toLocaleLowerCase();

    return lowerLeft.includes(lowerRight);
  }

  const check = (left || []).filter((s) =>
    (s || '').toLocaleLowerCase().includes((right || '').toLocaleLowerCase()),
  );
  return !!check.length;
}

export function containsAnyOf(left, right) {
  if (isString(left) && Array.isArray(right)) {
    const check = right.filter((s) =>
      (left || '').toLocaleLowerCase().includes((s || '').toLocaleLowerCase()),
    );
    return !!check.length;
  }

  if (Array.isArray(left) && Array.isArray(right)) {
    return right.some((criteriaValue) => {
      return left.some((s) => {
        const leftValue = isString(s) ? s.toLocaleLowerCase() : s;
        const safeCriteriaValue = isString(criteriaValue)
          ? criteriaValue.toLocaleLowerCase()
          : criteriaValue;
        return leftValue.includes(safeCriteriaValue);
      });
    });
  }

  return false;
}

export function isIn(left, right) {
  if (Array.isArray(left) && Array.isArray(right)) {
    return !!intersection(left, right).length;
  }

  return (right || []).includes(left);
}

export function isAbsoluteDate(right) {
  return isString(right) && dayjs(right).isValid();
}

export function isRelativeDate(right) {
  const regex = /^(now)[-+]([0-9]|[1-9][0-9])(s|h|d|w|m|M)/g;
  return isString(right) && regex.test(right);
}

export function setRelativeDate(right) {
  const dateUnitKey = right.substring(right.length - 1);
  const dateUnit = DATE_UNITS[dateUnitKey];
  if (!dateUnit) {
    throw new Error(`date unit ${dateUnitKey} not supported`);
  }
  const operation = right[3];
  const count = Number(right.substring(4, right.length - 1));
  let date;

  if (operation === '-') {
    date = dayjs().subtract(count, dateUnit).toJSON();
  } else {
    date = dayjs().add(count, dateUnit).toJSON();
  }

  return date;
}

export function isGreaterThan(left, right) {
  if (isRelativeDate(right)) {
    const relativeDate = setRelativeDate(right);
    return Date.parse(left) > Date.parse(relativeDate);
  }

  if (isAbsoluteDate(right)) {
    return Date.parse(left) > Date.parse(right);
  }

  return Number(left) > Number(right);
}

export function isGreaterThanOrEqual(left, right) {
  if (isRelativeDate(right)) {
    const relativeDate = setRelativeDate(right);
    return Date.parse(left) >= Date.parse(relativeDate);
  }

  if (isAbsoluteDate(right)) {
    return Date.parse(left) >= Date.parse(right);
  }

  return Number(left) >= Number(right);
}

export function isLessThan(left, right) {
  if (isRelativeDate(right)) {
    const relativeDate = setRelativeDate(right);
    return Date.parse(left) < Date.parse(relativeDate);
  }

  if (isAbsoluteDate(right)) {
    return Date.parse(left) < Date.parse(right);
  }

  return Number(left) < Number(right);
}

export function isLessThanOrEqual(left, right) {
  if (isRelativeDate(right)) {
    const relativeDate = setRelativeDate(right);
    return Date.parse(left) <= Date.parse(relativeDate);
  }

  if (isAbsoluteDate(right)) {
    return Date.parse(left) <= Date.parse(right);
  }

  return Number(left) <= Number(right);
}

export function evaluateDouble(op: AssistantRuleOperator, left, right) {
  switch (op) {
    case 'equals': {
      return equals(left, right);
    }
    case 'not_equals': {
      return !equals(left, right);
    }
    case 'gt': {
      return isGreaterThan(left, right);
    }
    case 'gte': {
      return isGreaterThanOrEqual(left, right);
    }
    case 'lt': {
      return isLessThan(left, right);
    }
    case 'lte': {
      return isLessThanOrEqual(left, right);
    }
    case 'in': {
      return isIn(left, right);
    }
    case 'not_in': {
      return !isIn(left, right);
    }
    case 'contains': {
      return contains(left, right);
    }
    case 'not_contains': {
      return !contains(left, right);
    }
    case 'contains_any_of': {
      return containsAnyOf(left, right);
    }
    case 'not_contains_any_of': {
      return !containsAnyOf(left, right);
    }
    default: {
      throw new Error(`operation ${op} not supported`);
    }
  }
}

export function evaluate(
  clause: Record<string, AssistantRuleCriteria>,
  context,
) {
  const path = Object.keys(clause)[0];
  const { operator, value } = clause[path];

  switch (operator) {
    case 'is_set':
    case 'not_set': {
      // const isDisabled = isDisabledAttribute(value[0], metadata);
      // if (isDisabled) {
      //   return true;
      // }
      const val = resolve(path, context);
      const result = evaluateSingle(operator, val);
      return result;
    }
    case 'equals':
    case 'not_equals':
    case 'gt':
    case 'gte':
    case 'lt':
    case 'lte':
    case 'in':
    case 'not_in':
    case 'contains':
    case 'not_contains':
    case 'contains_any_of':
    case 'not_contains_any_of': {
      // const isDisabled = isDisabledAttribute(value[0], metadata);
      // if (isDisabled) {
      //   return true;
      // }
      const left = resolve(path, context);
      const right = value;
      const result = evaluateDouble(operator, left, right);
      return result;
    }
    default: {
      throw new Error(`operation ${operator} not supported`);
    }
  }
}
export const allAndRules = (
  rule: AssistantRule,
  context: AssistantRulesContext,
) => {
  return rule.criteria.and?.every((criteria) => evaluate(criteria, context));
};
export const someOrRules = (
  rule: AssistantRule,
  context: AssistantRulesContext,
) => {
  const orRules = rule.criteria.or;
  if (!orRules || !orRules.length) {
    return true;
  }
  return !!orRules.find((criteria) => evaluate(criteria, context));
};

export const evaluateRule = (
  rule: AssistantRule,
  session: SessionDetails,
  customer: CustomerDetails,
) => {
  const context = {
    session,
    customer,
  };
  return allAndRules(rule, context) && someOrRules(rule, context);
};

export const getChatActionFromRule = (
  rule: AssistantRule,
): AssistantRuleAction | undefined => {
  return rule.actions?.find(
    (action) => action?.input?.pendingAssistant?.channel === 'chat',
  );
};
